import React from 'react';

import outlookLogoTiny from '../../../../../../../../../src/assets/images/integrations/outlook16.svg';
import outlookLogo from '../../../../../../../../../src/assets/images/integrations/outlook24.svg';
import {CrmToFrontCustomFieldMapping} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {buildMicrosoftDynamicsExternalUrl} from '../../../../../../../../../src/helpers/accountHelpers';
import {CrmFieldModel} from '../../../../../../../../../src/models/api/crmFieldModel';
import {
  CrmSyncCardDefaultFieldIdsEnum,
  CrmSyncIntegrationResourceTypesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {CrmSyncsPageConfig} from './crmSyncsPageConfig';
import {defaultIcons, maxCardsToSync} from './crmSyncsPageConfigHelpers';

/*
 * Constants.
 */

enum OutlookDirectoryDefaultCardFieldsEnum {
  DISPLAY_NAME = 'displayName',
  MAIL = 'mail',
}

const outlookDirectoryRequiredCardMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: OutlookDirectoryDefaultCardFieldsEnum.DISPLAY_NAME,
    customFieldId: CrmSyncCardDefaultFieldIdsEnum.NAME,
  },
];

const outlookDirectoryOptionalCardMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: OutlookDirectoryDefaultCardFieldsEnum.MAIL,
    customFieldId: CrmSyncCardDefaultFieldIdsEnum.EMAIL,
  },
];

/*
 * Config.
 */

export const outlookDirectoryConfig: CrmSyncsPageConfig = {
  logo: outlookLogo,
  logoSm: outlookLogo,
  logoTiny: outlookLogoTiny,
  displayName: 'Outlook directory',
  loginDescription: (
    <Trans id="crm-sync-page-outlook-directory-login-description">
      Outlook admin privileges required to sign in and grant permission. Once permissions are granted by an
      Outlook admin here, other Front admins can set up and configure the sync.
    </Trans>
  ),
  infoBoxDescription: (
    <Trans id="crm-sync-page-infobox-outlook-directory-additonal-content">
      Note that Outlook groups and distribution lists will also sync as contacts inside Front.
    </Trans>
  ),
  [CrmSyncIntegrationResourceTypesEnum.CARD]: {
    helpCenterLink: 'https://help.front.com/t/p8hsc8q',
    maxNumberToSync: maxCardsToSync,
    customTitle: <Trans id="crm-syncs-page-outlook-directory-section-title">Sync internal contacts</Trans>,
    customDescription: (
      <Trans id="crm-syncs-page-outlook-directory-section-description">
        Only syncs internal contacts from Outlook directory, not external contacts
      </Trans>
    ),
    customIconName: 'buildingFilledCompany',
    buildExternalUrl: buildMicrosoftDynamicsExternalUrl,
    syncOptionsTitle: null,
    syncOptions: {},
    syncMapping: {
      customTitle: (
        <Trans id="crm-syncs-page-outlook-directory-cards-mapping-title">
          Map how Outlook directory user attributes appear in Front
        </Trans>
      ),
      customDescription: (
        <Trans id="crm-syncs-page-outlook-directory-cards-mapping-description">
          Select which Outlook directory user attributes to sync to Front and map them to a Front contact
          custom field. Contacts will be matched via handles.
        </Trans>
      ),
      defaultCrmFieldType: 'string',
      computeRequiredMapping: () => outlookDirectoryRequiredCardMapping,
      computeOptionalMapping: () => outlookDirectoryOptionalCardMapping,
      computeCrmDefaultFieldIcon: (crmField?: CrmFieldModel) =>
        computeCrmCardDefaultFieldPlaceholderIcon(crmField?.id),
      computeCrmDefaultFieldPlaceholderName: computeCrmCardDefaultFieldPlaceholderName,
      computeCrmDefaultFieldPlaceholderIcon: (crmFieldId: string) =>
        computeCrmCardDefaultFieldPlaceholderIcon(crmFieldId, 'mentionSmall'),
    },
  },
};

/*
 * Helpers.
 */

function computeCrmCardDefaultFieldPlaceholderName(customFieldId: string) {
  switch (customFieldId) {
    case OutlookDirectoryDefaultCardFieldsEnum.DISPLAY_NAME:
      return 'Display Name';
    case OutlookDirectoryDefaultCardFieldsEnum.MAIL:
      return 'Email';
    default:
      return undefined;
  }
}

function computeCrmCardDefaultFieldPlaceholderIcon(crmFieldId?: string, defaultIconName?: IconName) {
  switch (crmFieldId) {
    case OutlookDirectoryDefaultCardFieldsEnum.DISPLAY_NAME:
      return 'contentSmall';
    case OutlookDirectoryDefaultCardFieldsEnum.MAIL:
      return defaultIcons.email;
    default:
      return defaultIconName;
  }
}
